/**
 * 图片高度限制工具 - 基于容器宽度动态计算图片最大高度
 *
 * 核心原则:
 * 1. 图片高度 ≤ 富文本编辑器容器宽度 × 1/2 (50%)
 * 2. 图片默认使用 display: inline-block 在同一行显示(由CSS控制)
 * 3. 图片相邻处不留缝隙(由CSS margin:0 控制)
 */

/**
 * 根据容器宽度,计算每张图片的最大高度
 * @param containerWidth - 富文本编辑器容器宽度(px)
 * @returns 每张图片的最大高度(px)
 */
export function calculateMaxImageHeight(containerWidth: number): number {
  // 第一原则: 高度 ≤ 容器宽度 × 1/2 (50%)
  const maxHeight = containerWidth * 0.5;
  return maxHeight;
}

/**
 * 检测相邻图片是否属于同一组（连续排列且无文字间隔）
 */
function detectImageGroups(container: HTMLElement): HTMLImageElement[][] {
  const allImages = Array.from(container.querySelectorAll('img')) as HTMLImageElement[];
  if (allImages.length === 0) return [];

  const groups: HTMLImageElement[][] = [[allImages[0]]];

  for (let i = 1; i < allImages.length; i++) {
    const prevImg = allImages[i - 1];
    const currImg = allImages[i];

    // 检查两张图片之间是否有文字节点
    let hasTextBetween = false;
    let node: Node | null = prevImg.nextSibling;
    while (node && node !== currImg) {
      if (node.nodeType === Node.TEXT_NODE && node.textContent?.trim()) {
        hasTextBetween = true;
        break;
      }
      if (node.nodeType === Node.ELEMENT_NODE) {
        const el = node as Element;
        if (el.tagName !== 'BR' && el.tagName !== 'SPAN' && el.textContent?.trim()) {
          hasTextBetween = true;
          break;
        }
      }
      node = node.nextSibling;
    }

    if (hasTextBetween) {
      groups.push([currImg]);
    } else {
      groups[groups.length - 1].push(currImg);
    }
  }

  return groups;
}

/**
 * 对单张图片应用高度和宽度约束
 * 规则1（始终生效）：maxHeight = 容器宽度 × 50%，高度受限时宽度按原始宽高比等比缩放
 * 规则2（仅多图组生效）：单张图片宽度上限 = 容器宽度的 50%，让多图能同行并排显示
 * 单图组时宽度不受限，只受规则1约束
 * @param isMultiImageGroup - 该图片是否属于多图组（组内有多张图片）
 * @param signal - AbortSignal，用于在组件卸载时取消待触发的 load 回调
 */
function applyConstraintsToImage(imgElement: HTMLImageElement, containerWidth: number, maxHeight: number, isMultiImageGroup: boolean, signal?: AbortSignal): void {
  // 如果已被中止（组件卸载），跳过
  if (signal?.aborted) return;

  // 规则1（始终生效）：设置最大高度
  imgElement.style.maxHeight = `${maxHeight}px`;

  // 如果图片已加载完成，根据宽高比计算宽度约束
  if (imgElement.complete && imgElement.naturalWidth > 0 && imgElement.naturalHeight > 0) {
    const aspectRatio = imgElement.naturalWidth / imgElement.naturalHeight;

    // 规则1：高度受限时，宽度按原始宽高比等比缩放
    // constrainedWidth = maxHeight × aspectRatio
    const constrainedWidth = maxHeight * aspectRatio;

    if (isMultiImageGroup) {
      // 规则2（仅多图组生效）：
      // 多图组中图片 maxWidth: 45%，同时满足高度约束
      const maxWidthByFlex = containerWidth * 0.45;
      imgElement.style.maxWidth = `${Math.min(constrainedWidth, maxWidthByFlex)}px`;
    } else {
      // 单图组：宽度不受限，只受规则1约束（按原始宽高比等比缩放）
      imgElement.style.maxWidth = `${constrainedWidth}px`;
    }
  } else {
    // 图片未加载完成，监听 load 事件后重新应用约束
    // 使用 AbortSignal 确保组件卸载时自动移除此监听器
    const onLoad = () => {
      // 安全检查：图片必须仍在 DOM 中才应用约束
      if (!imgElement.isConnected) return;
      applyConstraintsToImage(imgElement, containerWidth, maxHeight, isMultiImageGroup, signal);
    };
    imgElement.addEventListener('load', onLoad, { once: true, signal });
  }
}

/**
 * 为指定容器内的图片应用高度限制
 * @param container - 目标容器
 * @param signal - 可选的 AbortSignal，用于在组件卸载时取消待触发的 load 回调
 */
export function applyImageHeightLimit(container: HTMLElement, signal?: AbortSignal): void {
  // 如果已被中止或容器已不在文档树中，跳过
  if (signal?.aborted) return;
  if (!container.isConnected) return;

  const containerWidth = container.offsetWidth;

  // 容器宽度为0时跳过计算,避免除零错误
  if (containerWidth === 0) return;

  const maxHeight = calculateMaxImageHeight(containerWidth);

  // 先处理带 data-img-count 属性的图片（来自 Word 解析）
  // data-img-count 表示该题干共有多少张图片，count > 1 时为多图组
  const markedImages = container.querySelectorAll('img[data-img-count]');
  markedImages.forEach(img => {
    const imgCount = parseInt((img as HTMLImageElement).getAttribute('data-img-count') || '1');
    const isMultiImageGroup = imgCount > 1;
    applyConstraintsToImage(img as HTMLImageElement, containerWidth, maxHeight, isMultiImageGroup, signal);
  });

  // 再处理没有 data-img-count 属性的图片（手动上传）
  const unmarkedImages = container.querySelectorAll('img:not([data-img-count])');
  if (unmarkedImages.length === 0) return;

  // 根据图片组大小判断是否为多图组：组内有多张图片时 isMultiImageGroup = true
  const groups = detectImageGroups(container);
  groups.forEach(group => {
    const isMultiImageGroup = group.length > 1;
    group.forEach(img => {
      applyConstraintsToImage(img, containerWidth, maxHeight, isMultiImageGroup, signal);
    });
  });
}

/**
 * 监听容器大小变化,动态更新图片高度限制
 * @param container - 需要监听的容器元素
 * @returns 清理函数,用于组件卸载时断开观察并取消所有待触发的 load 回调
 */
export function watchImageHeight(container: HTMLElement): () => void {
  // AbortController 用于在清理时取消所有待触发的图片 load 回调
  const abortController = new AbortController();

  // 初始应用一次
  applyImageHeightLimit(container, abortController.signal);

  let rafId: number | null = null;

  const resizeObserver = new ResizeObserver(() => {
    // 如果容器已不在文档树中，立即断开观察器并返回
    if (!container.isConnected) {
      resizeObserver.disconnect();
      return;
    }
    // 使用 requestAnimationFrame 防抖，避免在同一帧内多次触发导致 loop 错误
    if (rafId !== null) return;
    rafId = requestAnimationFrame(() => {
      rafId = null;
      try {
        applyImageHeightLimit(container, abortController.signal);
      } catch (error) {
        // 静默忽略 ResizeObserver 回调中的异常，避免影响页面稳定性
        console.warn('ResizeObserver callback error:', error);
      }
    });
  });

  resizeObserver.observe(container);

  // 返回清理函数：同时断开 ResizeObserver、取消 rAF 和取消所有 load 监听器
  return () => {
    abortController.abort();
    if (rafId !== null) {
      cancelAnimationFrame(rafId);
      rafId = null;
    }
    resizeObserver.disconnect();
  };
}
